import { useEffect, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { PlatformMark, platformLabel } from './platform-chip'
import { useQueries } from '@tanstack/react-query'
import type { Holding } from '@/lib/api-types'
import { formatPct, formatPrice, formatTokenAmountShort, formatUsd, formatUsdShort } from '@/lib/format'
import { useTRPC } from '@/lib/trpc'
import { SOL_MINT, SOLANA_DEFAULT_TOKENS, tokenLogo } from '@/lib/default-tokens'
import { defaultToPicked, holdingToPicked, type PickedToken } from '@/lib/picked-token'
import { isAccountHolding } from '@/lib/networks'
import { useHoldingsFilter } from '@/lib/holdings-filter'
import { PIN, useScrolledSideways } from './pinned-column'
import { Flash } from './flash'
import type { Chain } from '../../../src/constants/chains'

const SOLANA = 'solana' as Chain

type Row = {
  key: string
  symbol: string
  name: string
  imageUrl?: string
  balance: number
  priceUsd?: number
  marketCapUsd?: number
  change24hPct?: number | null
  valueUsd?: number
  custody?: 'vault'
  /** how much sits on each platform (John, Nov 1 2026: all balances together, the split a click away) */
  platforms?: { platform: string; balance: string }[]
  decimals: number
  /** the row as the flows see it */
  token: PickedToken
}

/** the order of the places under a holding (John, Nov 1 2026: "MEXC | Coinbase | Binance | Vault"; Kraken and KuCoin before the vault, Nov 4 2026) */
const PLATFORM_ORDER = ['mexc', 'coinbase', 'binanceus', 'kraken', 'kucoin', 'vault']
const platformRank = (id: string) => {
  const index = PLATFORM_ORDER.indexOf(id)
  return index === -1 ? PLATFORM_ORDER.length : index
}

export function HoldingsTable({
  tokens,
  onSelect,
}: {
  tokens: Holding[]
  /** a row was clicked: open that token's actions */
  onSelect?: (token: PickedToken) => void
}) {
  const trpc = useTRPC()
  const filter = useHoldingsFilter()
  const sideways = useScrolledSideways()
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const toggle = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  // the account's rows: everything on Solana, plus the exchanges' ETH and BNB (Nov 3 2026), whose rows carry their home
  // chain; the trading wallet's own EVM rows, which the portfolio endpoint returns for the bot's home page, stay out
  const held = tokens.filter(isAccountHolding)
  const missingDefaults = SOLANA_DEFAULT_TOKENS.filter((d) => !held.some((t) => t.address === d.address))
  // Core tokens not held still show with a live price, like Sonar's token table.
  const prices = useQueries({
    queries: missingDefaults.map((d) => ({
      ...trpc.token.get.queryOptions({ chain: SOLANA, address: d.address }),
      staleTime: 60_000,
      refetchInterval: 60_000,
    })),
  })

  const rows: Row[] = held.map((t) => ({
    key: t.address,
    symbol: (t.symbol ?? '').toUpperCase(),
    name: t.name,
    imageUrl: tokenLogo(t.isNative ? SOL_MINT : t.address, t.imageUrl),
    balance: Number(t.balance) / 10 ** t.decimals,
    priceUsd: t.priceUsd,
    marketCapUsd: t.marketCapUsd,
    change24hPct: t.priceChange24hPct,
    valueUsd: t.valueUsd,
    custody: t.custody,
    platforms: t.platforms,
    decimals: t.decimals,
    token: holdingToPicked(t),
  }))
  missingDefaults.forEach((d, i) => {
    const market = prices[i]?.data
    rows.push({
      key: d.address,
      symbol: d.symbol,
      name: d.name,
      imageUrl: d.imageUrl,
      balance: 0,
      priceUsd: market?.priceUsd,
      marketCapUsd: market?.marketCapUsd,
      change24hPct: market?.priceChange24hPct,
      valueUsd: 0,
      decimals: d.decimals,
      token: defaultToPicked(d, market),
    })
  })
  // native first, then by value, then core tokens by their list order
  rows.sort((a, b) => {
    if (a.symbol === 'SOL') return -1
    if (b.symbol === 'SOL') return 1
    return (b.valueUsd ?? 0) - (a.valueUsd ?? 0)
  })
  // small balances stay out (John, Oct 30 2026): the chain's coin always shows, a row with no price yet is not judged
  const minUsd = filter.hideSmall ? filter.minUsd : 0
  const visible = rows.filter((row) => row.symbol === 'SOL' || row.valueUsd === undefined || row.valueUsd >= minUsd)

  // sideways only: the page scrolls down; a sideways overscroll stays here instead of turning into the browser's back swipe
  return (
    <div onScroll={sideways.onScroll} className="overflow-x-auto overscroll-x-contain">
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-muted">
          <tr>
            <th className={`${PIN} ${sideways.edge} w-max px-4 py-2 font-medium`}>Token</th>
            <th className="px-4 py-2 text-right font-medium">Balance</th>
            <th className="px-4 py-2 text-right font-medium">Value</th>
            <th className="px-4 py-2 text-right font-medium">Price</th>
            <th className="px-4 py-2 text-right font-medium">MC</th>
            <th className="px-4 py-2 text-right font-medium">24h</th>
          </tr>
        </thead>
        <tbody>
          {visible.map((row) => {
            const change = row.change24hPct
            // the biggest balance first (John, Nov 2 2026), the fixed order only between equals
            const split = row.platforms && row.platforms.length > 0 ? [...row.platforms].sort((a, b) => Number(b.balance) - Number(a.balance) || platformRank(a.platform) - platformRank(b.platform)) : null
            const open = split !== null && expanded.has(row.key)
            return [
              <tr
                key={row.key}
                className={`group border-t border-line ${onSelect ? 'cursor-pointer hover:bg-surface-2' : ''}`}
                onClick={onSelect ? () => onSelect(row.token) : undefined}
                onKeyDown={onSelect ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(row.token) } } : undefined}
                tabIndex={onSelect ? 0 : undefined}
                role={onSelect ? 'button' : undefined}
              >
                {/* the pinned cell paints its own background, so it follows the row's hover by hand */}
                <td className={`${PIN} ${sideways.edge} w-max px-4 py-3 ${onSelect ? 'group-hover:bg-surface-2' : ''}`}>
                  {split ? (
                    // the whole token, icon to name, opens the split, not just the arrow (John, Nov 1 2026)
                    // the flex row lives in a span inside the button, not on the button itself: WebKit lays a flex button
                    // out through an inner box of its own and gave the name 32px (John, Nov 2 2026: "Sol…", "He…" on his phone)
                    <button
                      type="button"
                      className="-m-1 block rounded p-1 text-left"
                      title={open ? 'Hide where it is held' : 'Where it is held'}
                      aria-expanded={open}
                      onClick={(e) => { e.stopPropagation(); toggle(row.key) }}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <span className="flex items-center gap-3">
                        <TokenIcon symbol={row.symbol} imageUrl={row.imageUrl} />
                        <TokenCellText symbol={row.symbol} name={row.name} chevron={open} />
                      </span>
                    </button>
                  ) : (
                    <div className="flex items-center gap-3">
                      <TokenIcon symbol={row.symbol} imageUrl={row.imageUrl} />
                      <TokenCellText symbol={row.symbol} name={row.name} />
                    </div>
                  )}
                </td>
                <td className={`px-4 py-3 text-right tabular-nums ${row.balance === 0 ? 'text-muted' : ''}`}>{formatTokenAmountShort(row.balance, row.symbol)}</td>
                {/* a value or price that moved since the last poll tints for a moment (Nov 4 2026, see flash.tsx) */}
                <td className={`px-4 py-3 text-right tabular-nums ${row.balance === 0 ? 'text-muted' : ''}`}><Flash value={row.valueUsd} text={formatUsdShort(row.valueUsd)} /></td>
                <td className="px-4 py-3 text-right tabular-nums text-muted"><Flash value={row.priceUsd} text={formatPrice(row.priceUsd)} /></td>
                <td className="px-4 py-3 text-right tabular-nums text-muted">{row.marketCapUsd ? formatUsd(row.marketCapUsd, true) : '–'}</td>
                <td className={`px-4 py-3 text-right tabular-nums ${change === undefined || change === null ? 'text-muted' : change >= 0 ? 'text-positive' : 'text-negative'}`}>{formatPct(change)}</td>
              </tr>,
              open && split ? (
                <tr key={`${row.key}:platforms`}>
                  <td colSpan={6} className="px-4 pb-3 pt-0">
                    {/* the same size as the place menus, the amount right beside the name (John, Nov 1 2026: "they're too far away") */}
                    <div className="ml-10 grid gap-1.5 text-sm text-muted">
                      {split.map((entry) => {
                        const amount = Number(entry.balance) / 10 ** row.decimals
                        return (
                          <div key={entry.platform} className="flex items-center gap-2">
                            <PlatformMark id={entry.platform} size="size-4" />
                            <span className="min-w-24">{platformLabel(entry.platform)}</span>
                            <span className="tabular-nums text-text">{formatTokenAmountShort(amount, row.symbol)}</span>
                            {row.priceUsd !== undefined ? <span className="tabular-nums">({formatUsdShort(amount * row.priceUsd)})</span> : null}
                          </div>
                        )
                      })}
                    </div>
                  </td>
                </tr>
              ) : null,
            ]
          })}
        </tbody>
      </table>
    </div>
  )
}

/**
 * The ticker over the name. The cell is `w-max` and this block is not shrinkable: WebKit sizes a table column without
 * an ellipsis-cut name and a `min-w-0` flex item (Safari showed "He…", John, Nov 2 2026), so the column is told to fit
 * its content; only a very long name is cut, at ten rem, so the pinned column cannot swallow a phone screen.
 */
function TokenCellText({ symbol, name, chevron }: { symbol: string; name: string; chevron?: boolean }) {
  return (
    <span className="block">
      <span className="flex items-center gap-1.5 whitespace-nowrap font-medium">
        {symbol}
        {chevron !== undefined ? <ChevronDown className={`size-3.5 text-muted transition-transform ${chevron ? 'rotate-180' : ''}`} /> : null}
      </span>
      <span className="block max-w-[10rem] truncate text-xs text-muted" title={name}>
        {name}
      </span>
    </span>
  )
}

export function TokenIcon({ symbol, imageUrl, size = 'size-7', badge }: { symbol: string; imageUrl?: string; size?: string; /** a chain's mark in the corner, the way wallets show a coin on a chain other than its own (ETH on Base, Nov 4 2026) */ badge?: string }) {
  const [failed, setFailed] = useState(false)
  useEffect(() => setFailed(false), [imageUrl])
  const icon =
    imageUrl && !failed ? (
      <img src={imageUrl} alt="" className={`${size} shrink-0 rounded-full object-cover`} onError={() => setFailed(true)} />
    ) : (
      // no logo, or a dead logo URL: initials in a neutral disc
      <span className={`flex ${size} shrink-0 items-center justify-center rounded-full bg-surface-2 text-[10px] font-semibold text-muted`}>
        {symbol.slice(0, 2).toUpperCase()}
      </span>
    )
  if (!badge) return icon
  return (
    <span className={`relative ${size} shrink-0`}>
      {icon}
      <img src={badge} alt="" className="absolute -bottom-0.5 -right-0.5 size-[46%] rounded-full object-cover ring-2 ring-surface" />
    </span>
  )
}
